/**
 * AnnouncementBanner
 *
 * Site-wide banner driven by the admin `announcements` collection. Shows the
 * most recent active announcement; dismissible per-announcement (remembered
 * in localStorage).
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';

interface ActiveAnnouncement {
  id: string;
  message: string;
  type: 'info' | 'promo' | 'warning';
  ctaLabel?: string;
  ctaUrl?: string;
}

const STYLES: Record<ActiveAnnouncement['type'], string> = {
  info: 'bg-blue-600 text-white',
  promo: 'bg-primary-600 text-white',
  warning: 'bg-amber-500 text-gray-900',
};

const DISMISS_KEY = 'darak_dismissed_announcement';

export const AnnouncementBanner: React.FC = () => {
  const [announcement, setAnnouncement] = useState<ActiveAnnouncement | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, 'announcements'),
            where('active', '==', true),
            orderBy('createdAt', 'desc'),
            limit(1)
          )
        );
        if (cancelled || snap.empty) return;
        const d = snap.docs[0];
        const data = d.data();
        if (localStorage.getItem(DISMISS_KEY) === d.id) return;
        setAnnouncement({
          id: d.id,
          message: data.message || '',
          type: (data.type as ActiveAnnouncement['type']) || 'info',
          ctaLabel: data.ctaLabel || undefined,
          ctaUrl: data.ctaUrl || undefined,
        });
      } catch {
        // Banner is best-effort; ignore fetch/index errors.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!announcement) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, announcement.id);
    setAnnouncement(null);
  };

  const isInternal = announcement.ctaUrl?.startsWith('/');

  return (
    <div className={`${STYLES[announcement.type]} text-sm relative`}>
      <div className="max-w-7xl mx-auto px-10 py-2 flex items-center justify-center gap-3 text-center">
        <span>{announcement.message}</span>
        {announcement.ctaLabel && announcement.ctaUrl && (
          isInternal ? (
            <Link
              to={announcement.ctaUrl}
              className="font-semibold underline underline-offset-2 whitespace-nowrap"
            >
              {announcement.ctaLabel}
            </Link>
          ) : (
            <a
              href={announcement.ctaUrl}
              className="font-semibold underline underline-offset-2 whitespace-nowrap"
            >
              {announcement.ctaLabel}
            </a>
          )
        )}
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute right-4 opacity-80 hover:opacity-100"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
